import { useState, useEffect, useRef, useCallback } from 'react';
import { adminApi } from '../../utils/api';
import { Button, Alert, Spinner, Modal, Input, Textarea } from './shared';

function formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const TEXT_EXTENSIONS = new Set([
    'md', 'txt', 'json', 'js', 'ts', 'jsx', 'tsx', 'py', 'yaml', 'yml',
    'xml', 'html', 'css', 'csv', 'sh', 'bash', 'cfg', 'ini', 'toml',
    'rb', 'rs', 'go', 'java', 'c', 'h', 'cpp', 'hpp', 'sql', 'env',
    'gitignore', 'dockerfile', 'makefile', 'lock', 'log',
]);

function isTextType(ct, path) {
    if (ct && (ct.startsWith('text/') || ct === 'application/json' || ct === 'application/javascript' || ct.includes('yaml') || ct.includes('xml'))) {
        return true;
    }
    if (path) {
        const ext = path.split('.').pop()?.toLowerCase();
        return TEXT_EXTENSIONS.has(ext);
    }
    return false;
}

function isKeepFile(path) {
    return path.endsWith('/.keep') || path === '.keep';
}

function buildTree(resources) {
    const root = { name: '', path: '', children: {}, files: [] };

    const ensureFolder = (folderPath) => {
        const parts = folderPath.split('/');
        let node = root;
        let currentPath = '';
        for (const part of parts) {
            currentPath = currentPath ? `${currentPath}/${part}` : part;
            if (!node.children[part]) {
                node.children[part] = { name: part, path: currentPath, children: {}, files: [] };
            }
            node = node.children[part];
        }
        return node;
    };

    for (const r of resources) {
        if (isKeepFile(r.path)) {
            ensureFolder(r.path.replace(/\/.keep$/, ''));
            continue;
        }
        const parts = r.path.split('/');
        if (parts.length < 2) {
            root.files.push(r);
            continue;
        }
        parts.pop();
        const folderPath = parts.join('/');
        const folder = ensureFolder(folderPath);
        folder.files.push(r);
    }

    return root;
}

function countItems(node) {
    let count = node.files.length;
    for (const child of Object.values(node.children)) {
        count += countItems(child);
    }
    return count;
}

const ChevronIcon = ({ expanded }) => (
    <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
);

const FolderIcon = ({ open }) => (
    <svg className="w-4 h-4 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        {open ? (
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776" />
        ) : (
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
        )}
    </svg>
);

const FileIcon = () => (
    <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
);

function TreeFolder({ node, depth, expanded, selected, onToggle, onClick, onFileClick, onContextMenu }) {
    const total = countItems(node);
    const isOpen = expanded[node.path] !== false;
    const isSelected = selected.has(`folder:${node.path}`);
    const childFolders = Object.values(node.children).sort((a, b) => a.name.localeCompare(b.name));
    const files = [...node.files].sort((a, b) => a.path.localeCompare(b.path));

    return (
        <>
            <div
                className={`flex items-center gap-1 py-1 px-2 rounded cursor-pointer select-none ${isSelected ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-gray-100'}`}
                style={{ paddingLeft: `${depth * 16 + 8}px` }}
                onClick={(e) => { onClick(e, 'folder', node); onToggle(node.path); }}
                onContextMenu={(e) => { onContextMenu(e, 'folder', node); }}
            >
                <ChevronIcon expanded={isOpen} />
                <FolderIcon open={isOpen} />
                <span className="text-sm text-gray-800 truncate">{node.name}</span>
                <span className="text-[10px] text-gray-400 ml-1">{total}</span>
            </div>
            {isOpen && (
                <>
                    {childFolders.map(child => (
                        <TreeFolder
                            key={child.path}
                            node={child}
                            depth={depth + 1}
                            expanded={expanded}
                            selected={selected}
                            onToggle={onToggle}
                            onClick={onClick}
                            onFileClick={onFileClick}
                            onContextMenu={onContextMenu}
                        />
                    ))}
                    {files.map(file => (
                        <TreeFile
                            key={file.path}
                            file={file}
                            depth={depth + 1}
                            selected={selected}
                            onClick={onClick}
                            onDoubleClick={onFileClick}
                            onContextMenu={onContextMenu}
                        />
                    ))}
                    {childFolders.length === 0 && files.length === 0 && (
                        <div
                            className="text-xs text-gray-400 italic py-0.5"
                            style={{ paddingLeft: `${(depth + 1) * 16 + 28}px` }}
                        >
                            empty
                        </div>
                    )}
                </>
            )}
        </>
    );
}

function TreeFile({ file, depth, selected, onClick, onDoubleClick, onContextMenu }) {
    const name = file.path.split('/').pop();
    const isSelected = selected.has(`file:${file.path}`);
    return (
        <div
            className={`flex items-center gap-1.5 py-1 px-2 rounded cursor-pointer select-none ${isSelected ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-gray-100'}`}
            style={{ paddingLeft: `${depth * 16 + 22}px` }}
            onClick={(e) => { onClick(e, 'file', file); }}
            onDoubleClick={() => { onDoubleClick(file); }}
            onContextMenu={(e) => { onContextMenu(e, 'file', file); }}
        >
            <FileIcon />
            <span className="text-sm text-gray-700 truncate">{name}</span>
            <span className="text-[10px] text-gray-400 shrink-0 ml-auto">{formatSize(file.size)}</span>
        </div>
    );
}

function ContextMenu({ x, y, items, onClose }) {
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onClose();
        };
        document.addEventListener('mousedown', handler);
        document.addEventListener('contextmenu', handler);
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('contextmenu', handler);
        };
    }, [onClose]);

    return (
        <div
            ref={ref}
            className="fixed z-50 min-w-[160px] bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-sm"
            style={{ left: x, top: y }}
        >
            {items.map((item, i) =>
                item.separator ? (
                    <div key={i} className="border-t border-gray-100 my-1" />
                ) : (
                    <button
                        key={i}
                        className={`w-full text-left px-3 py-1.5 hover:bg-gray-100 ${item.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700'}`}
                        onClick={() => { item.action(); onClose(); }}
                    >
                        {item.label}
                    </button>
                )
            )}
        </div>
    );
}

export default function SkillResourcePanel({ skillId, readOnly = false }) {
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [selected, setSelected] = useState(new Set());
    const [contextMenu, setContextMenu] = useState(null);
    const [openFile, setOpenFile] = useState(null);
    const [fileContent, setFileContent] = useState('');
    const [fileLoading, setFileLoading] = useState(false);
    const [fileMode, setFileMode] = useState('view');
    const [saving, setSaving] = useState(false);
    const [folderModal, setFolderModal] = useState({ open: false, parent: null });
    const [folderName, setFolderName] = useState('');
    const [creatingFolder, setCreatingFolder] = useState(false);
    const [renameModal, setRenameModal] = useState({ open: false, type: null, item: null });
    const [renameName, setRenameName] = useState('');
    const [renaming, setRenaming] = useState(false);
    const folderInputRef = useRef(null);
    const fileInputRef = useRef(null);
    const subFolderInputRef = useRef(null);
    const uploadTargetFolder = useRef(null);

    const loadResources = useCallback(async () => {
        setLoading(true);
        try {
            const data = await adminApi.listSkillResources(skillId);
            setResources(data.resources || []);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load resources');
        } finally {
            setLoading(false);
        }
    }, [skillId]);

    useEffect(() => { loadResources(); }, [loadResources]);

    const handleSelect = (e, type, item) => {
        const key = type === 'folder' ? `folder:${item.path}` : `file:${item.path}`;
        const isMulti = e.metaKey || e.ctrlKey;
        if (isMulti) {
            setSelected(prev => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                return next;
            });
        } else {
            setSelected(new Set([key]));
        }
    };

    const handleFolderUpload = async (e) => {
        const fileList = Array.from(e.target.files || []);
        if (!fileList.length) return;

        const files = [];
        const paths = [];
        for (const file of fileList) {
            files.push(file);
            paths.push(file.webkitRelativePath || file.name);
        }

        setUploading(true);
        setError(null);
        try {
            const result = await adminApi.uploadSkillResources(skillId, files, null, paths);
            if (result.errors?.length) {
                setError(result.errors.map(e => `${e.file}: ${e.error}`).join(', '));
            }
            setSuccess(`Uploaded ${result.uploaded?.length || 0} file(s)`);
            setTimeout(() => setSuccess(null), 3000);
            await loadResources();
        } catch (err) {
            setError(err.message);
        } finally {
            setUploading(false);
            if (folderInputRef.current) folderInputRef.current.value = '';
        }
    };

    const handleFilesUpload = async (e) => {
        const folder = uploadTargetFolder.current;
        if (!folder) return;
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        setError(null);
        try {
            const result = await adminApi.uploadSkillResources(skillId, files, folder);
            if (result.errors?.length) {
                setError(result.errors.map(e => `${e.file}: ${e.error}`).join(', '));
            }
            setSuccess(`Uploaded ${result.uploaded?.length || 0} file(s)`);
            setTimeout(() => setSuccess(null), 3000);
            await loadResources();
        } catch (err) {
            setError(err.message);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleSubFolderUpload = async (e) => {
        const targetFolder = uploadTargetFolder.current;
        if (!targetFolder) return;
        const fileList = Array.from(e.target.files || []);
        if (!fileList.length) return;

        const files = [];
        const paths = [];
        for (const file of fileList) {
            const relativePath = file.webkitRelativePath || file.name;
            files.push(file);
            paths.push(`${targetFolder}/${relativePath}`);
        }

        setUploading(true);
        setError(null);
        try {
            const result = await adminApi.uploadSkillResources(skillId, files, null, paths);
            if (result.errors?.length) {
                setError(result.errors.map(e => `${e.file}: ${e.error}`).join(', '));
            }
            setSuccess(`Uploaded ${result.uploaded?.length || 0} file(s)`);
            setTimeout(() => setSuccess(null), 3000);
            await loadResources();
        } catch (err) {
            setError(err.message);
        } finally {
            setUploading(false);
            if (subFolderInputRef.current) subFolderInputRef.current.value = '';
        }
    };

    const handleDelete = async (path) => {
        if (!confirm(`Delete "${path}"?`)) return;
        try {
            await adminApi.deleteSkillResource(skillId, path);
            setSelected(new Set());
            setSuccess(`Deleted ${path}`);
            setTimeout(() => setSuccess(null), 3000);
            await loadResources();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteFolder = async (folderPath) => {
        if (!confirm(`Delete folder "${folderPath}" and all its contents?`)) return;
        try {
            const result = await adminApi.deleteSkillFolder(skillId, folderPath);
            setSelected(new Set());
            setSuccess(`Deleted folder "${folderPath}" (${result.deleted} files)`);
            setTimeout(() => setSuccess(null), 3000);
            await loadResources();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteSelected = async () => {
        const filePaths = [...selected].filter(k => k.startsWith('file:')).map(k => k.slice(5));
        if (!filePaths.length) return;
        if (!confirm(`Delete ${filePaths.length} selected file(s)?`)) return;
        try {
            await Promise.all(filePaths.map(p => adminApi.deleteSkillResource(skillId, p)));
            setSelected(new Set());
            setSuccess(`Deleted ${filePaths.length} file(s)`);
            setTimeout(() => setSuccess(null), 3000);
            await loadResources();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleOpenFile = async (resource, mode = 'view') => {
        if (!isTextType(resource.content_type, resource.path)) return;
        setOpenFile(resource);
        setFileMode(mode);
        setFileLoading(true);
        try {
            const response = await adminApi.getSkillResource(skillId, resource.path);
            if (response instanceof Response) {
                setFileContent(await response.text());
            } else {
                setFileContent(typeof response === 'string' ? response : JSON.stringify(response, null, 2));
            }
        } catch (err) {
            setFileContent(`Error: ${err.message}`);
        } finally {
            setFileLoading(false);
        }
    };

    const handleCloseFile = () => {
        setOpenFile(null);
        setFileContent('');
        setFileMode('view');
    };

    const handleSaveFile = async () => {
        if (!openFile) return;
        setSaving(true);
        try {
            const blob = new Blob([fileContent], { type: openFile.content_type || 'text/plain' });
            const file = new File([blob], openFile.path.split('/').pop(), { type: blob.type });
            await adminApi.updateSkillResource(skillId, openFile.path, file);
            setSuccess(`Saved ${openFile.path}`);
            setTimeout(() => setSuccess(null), 3000);
            handleCloseFile();
            await loadResources();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleCreateFolder = async (e) => {
        e.preventDefault();
        const name = folderName.trim();
        if (!name) return;

        const fullPath = folderModal.parent ? `${folderModal.parent}/${name}` : name;
        setCreatingFolder(true);
        try {
            await adminApi.createSkillFolder(skillId, fullPath);
            setSuccess(`Created folder "${fullPath}"`);
            setTimeout(() => setSuccess(null), 3000);
            setFolderModal({ open: false, parent: null });
            setFolderName('');
            await loadResources();
        } catch (err) {
            setError(err.message);
        } finally {
            setCreatingFolder(false);
        }
    };

    const handleRename = async (e) => {
        e.preventDefault();
        const name = renameName.trim();
        if (!name || !renameModal.item) return;

        setRenaming(true);
        try {
            if (renameModal.type === 'folder') {
                await adminApi.renameSkillFolder(skillId, renameModal.item.path, name);
                setSuccess(`Renamed folder to "${name}"`);
            } else {
                const parts = renameModal.item.path.split('/');
                parts[parts.length - 1] = name;
                const newPath = parts.join('/');
                await adminApi.renameSkillResource(skillId, renameModal.item.path, newPath);
                setSuccess(`Renamed to "${name}"`);
            }
            setTimeout(() => setSuccess(null), 3000);
            setRenameModal({ open: false, type: null, item: null });
            setRenameName('');
            await loadResources();
        } catch (err) {
            setError(err.message);
        } finally {
            setRenaming(false);
        }
    };

    const toggleFolder = (path) => {
        setExpanded(prev => ({ ...prev, [path]: prev[path] === false ? true : false }));
    };

    const handleContextMenu = (e, type, item) => {
        e.preventDefault();
        e.stopPropagation();

        const key = type === 'folder' ? `folder:${item.path}` : `file:${item.path}`;
        let current = selected;
        if (!selected.has(key)) {
            current = new Set([key]);
            setSelected(current);
        }

        const selectedFiles = [...current].filter(k => k.startsWith('file:')).map(k => k.slice(5));
        const hasMultiSelect = selectedFiles.length > 1;
        const menuItems = [];

        if (type === 'folder') {
            if (!readOnly) {
                menuItems.push({
                    label: 'New Sub-folder',
                    action: () => { setFolderModal({ open: true, parent: item.path }); setFolderName(''); },
                });
                menuItems.push({
                    label: 'Upload Files Here',
                    action: () => {
                        uploadTargetFolder.current = item.path;
                        fileInputRef.current?.click();
                    },
                });
                menuItems.push({
                    label: 'Upload Folder Here',
                    action: () => {
                        uploadTargetFolder.current = item.path;
                        subFolderInputRef.current?.click();
                    },
                });
                menuItems.push({ separator: true });
                menuItems.push({
                    label: 'Rename',
                    action: () => { setRenameModal({ open: true, type: 'folder', item }); setRenameName(item.name); },
                });
                menuItems.push({ label: 'Delete Folder', danger: true, action: () => handleDeleteFolder(item.path) });
            }
        }

        if (type === 'file') {
            const isSkillMd = item.path === 'SKILL.md';
            if (hasMultiSelect && !readOnly && !isSkillMd) {
                menuItems.push({ label: `Delete ${selectedFiles.length} Files`, danger: true, action: handleDeleteSelected });
            } else {
                if (isTextType(item.content_type, item.path)) {
                    menuItems.push({ label: 'View', action: () => handleOpenFile(item, 'view') });
                    if (!readOnly && !isSkillMd) {
                        menuItems.push({ label: 'Edit', action: () => handleOpenFile(item, 'edit') });
                    }
                }
                if (!readOnly && !isSkillMd) {
                    menuItems.push({ separator: true });
                    menuItems.push({
                        label: 'Rename',
                        action: () => { setRenameModal({ open: true, type: 'file', item }); setRenameName(item.path.split('/').pop()); },
                    });
                    menuItems.push({ label: 'Delete', danger: true, action: () => handleDelete(item.path) });
                }
            }
        }

        if (menuItems.length === 0) return;

        setContextMenu({ x: e.clientX, y: e.clientY, items: menuItems });
    };

    const closeContextMenu = useCallback(() => {
        setContextMenu(null);
    }, []);

    if (loading) return <Spinner size="sm" text="Loading resources..." />;

    const tree = buildTree(resources);
    const topFolders = Object.values(tree.children).sort((a, b) => a.name.localeCompare(b.name));
    const rootFiles = tree.files.sort((a, b) => a.path.localeCompare(b.path));
    const hasContent = topFolders.length > 0 || rootFiles.length > 0;

    return (
        <div className="space-y-3">
            {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess(null)}>{success}</Alert>}

            {!readOnly && (
                <div className="flex items-center gap-2">
                    <input
                        ref={folderInputRef}
                        type="file"
                        className="hidden"
                        webkitdirectory=""
                        directory=""
                        multiple
                        onChange={handleFolderUpload}
                        disabled={uploading}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        multiple
                        onChange={handleFilesUpload}
                        disabled={uploading}
                    />
                    <input
                        ref={subFolderInputRef}
                        type="file"
                        className="hidden"
                        webkitdirectory=""
                        directory=""
                        multiple
                        onChange={handleSubFolderUpload}
                        disabled={uploading}
                    />
                    <Button size="sm" disabled={uploading} onClick={() => folderInputRef.current?.click()}>
                        {uploading ? 'Uploading...' : 'Upload Folder'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setFolderModal({ open: true, parent: null }); setFolderName(''); }}>
                        New Folder
                    </Button>
                </div>
            )}

            {hasContent ? (
                <div
                    className="border border-gray-200 rounded-lg bg-white py-1"
                    onClick={(e) => { if (e.target === e.currentTarget) setSelected(new Set()); }}
                    onContextMenu={(e) => {
                        if (e.target === e.currentTarget && !readOnly) {
                            e.preventDefault();
                            setContextMenu({
                                x: e.clientX,
                                y: e.clientY,
                                items: [
                                    {
                                        label: 'New Folder',
                                        action: () => { setFolderModal({ open: true, parent: null }); setFolderName(''); },
                                    },
                                ],
                            });
                        }
                    }}
                >
                    {rootFiles.map(file => (
                        <TreeFile
                            key={file.path}
                            file={file}
                            depth={0}
                            selected={selected}
                            onClick={(e) => handleSelect(e, 'file', file)}
                            onDoubleClick={() => handleOpenFile(file, (readOnly || file.path === 'SKILL.md') ? 'view' : 'edit')}
                            onContextMenu={(e) => handleContextMenu(e, 'file', file)}
                        />
                    ))}
                    {topFolders.map(folder => (
                        <TreeFolder
                            key={folder.path}
                            node={folder}
                            depth={0}
                            expanded={expanded}
                            selected={selected}
                            onToggle={toggleFolder}
                            onClick={handleSelect}
                            onFileClick={(file) => handleOpenFile(file, readOnly ? 'view' : 'edit')}
                            onContextMenu={handleContextMenu}
                        />
                    ))}
                </div>
            ) : (
                <p className="text-sm text-gray-400 py-4 text-center">No resources yet</p>
            )}

            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={contextMenu.items}
                    onClose={closeContextMenu}
                />
            )}

            <Modal
                open={!!openFile}
                title={openFile?.path || ''}
                onClose={handleCloseFile}
                size="xl"
            >
                {fileLoading ? (
                    <Modal.Body><Spinner size="sm" /></Modal.Body>
                ) : (
                    <>
                        <div className="flex items-center justify-between px-6 py-2 border-b border-gray-100">
                            <div className="flex items-center gap-1 bg-gray-100 rounded-md p-0.5">
                                <button
                                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${fileMode === 'view' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                    onClick={() => setFileMode('view')}
                                >
                                    View
                                </button>
                                {!readOnly && openFile?.path !== 'SKILL.md' && (
                                    <button
                                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${fileMode === 'edit' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                        onClick={() => setFileMode('edit')}
                                    >
                                        Edit
                                    </button>
                                )}
                            </div>
                            {fileMode === 'edit' && (
                                <Button size="sm" loading={saving} onClick={handleSaveFile}>Save</Button>
                            )}
                        </div>
                        <Modal.Body>
                            {fileMode === 'view' ? (
                                <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-800 whitespace-pre-wrap overflow-y-auto max-h-[60vh]">
                                    {fileContent}
                                </pre>
                            ) : (
                                <Textarea
                                    monospace
                                    rows={24}
                                    value={fileContent}
                                    onChange={(e) => setFileContent(e.target.value)}
                                />
                            )}
                        </Modal.Body>
                    </>
                )}
            </Modal>

            <Modal
                open={folderModal.open}
                title={folderModal.parent ? `New sub-folder in "${folderModal.parent}"` : 'New Folder'}
                onClose={() => setFolderModal({ open: false, parent: null })}
                size="sm"
            >
                <form onSubmit={handleCreateFolder}>
                    <Modal.Body>
                        <Input
                            label="Folder name"
                            value={folderName}
                            onChange={(e) => setFolderName(e.target.value.replace(/[^a-zA-Z0-9._-]/g, '-'))}
                            placeholder="e.g. scripts"
                            required
                            autoFocus
                        />
                        {folderModal.parent && (
                            <p className="text-xs text-gray-500">
                                Path: {folderModal.parent}/{folderName || '...'}
                            </p>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="ghost" type="button" onClick={() => setFolderModal({ open: false, parent: null })}>Cancel</Button>
                        <Button type="submit" size="sm" loading={creatingFolder}>Create</Button>
                    </Modal.Footer>
                </form>
            </Modal>

            <Modal
                open={renameModal.open}
                title={renameModal.type === 'folder' ? 'Rename Folder' : 'Rename File'}
                onClose={() => setRenameModal({ open: false, type: null, item: null })}
                size="sm"
            >
                <form onSubmit={handleRename}>
                    <Modal.Body>
                        <Input
                            label="New name"
                            value={renameName}
                            onChange={(e) => setRenameName(e.target.value.replace(/[^a-zA-Z0-9._-]/g, '-'))}
                            required
                            autoFocus
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="ghost" type="button" onClick={() => setRenameModal({ open: false, type: null, item: null })}>Cancel</Button>
                        <Button type="submit" size="sm" loading={renaming}>Rename</Button>
                    </Modal.Footer>
                </form>
            </Modal>
        </div>
    );
}
